import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { apiErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { ArrowLeft, Plus, Trash2, Check, Globe2, Calendar, GripVertical, MapPin } from "lucide-react";
import { DndContext, closestCenter, PointerSensor, TouchSensor, KeyboardSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove, sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const uid = () => (crypto.randomUUID ? crypto.randomUUID() : String(Math.random()));
const emptyCountry = () => ({ key: uid(), name: "", city: "", arrive_date: "", depart_date: "", highlight: "", currency: "", language: "" });

export default function BuildTrip() {
  const { config, refreshConfig, refreshOrg, refreshTrip } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [trip, setTrip] = useState({
    trip_name: config?.trip_name || "",
    start_date: config?.start_date || "",
    end_date: config?.end_date || "",
    currency: config?.currency || "USD",
    budget_goal: config?.budget_goal_cents ? config.budget_goal_cents / 100 : "",
    origin_country: config?.origin_country || "",
    origin_city: config?.origin_city || "",
  });
  const [countries, setCountries] = useState([emptyCountry()]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 180, tolerance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const setC = (key, k, v) => setCountries((cs) => cs.map((c) => (c.key === key ? { ...c, [k]: v } : c)));
  const addC = () => setCountries((cs) => [...cs, emptyCountry()]);
  const delC = (key) => setCountries((cs) => cs.filter((c) => c.key !== key));
  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    setCountries((cs) => {
      const oldI = cs.findIndex((c) => c.key === active.id);
      const newI = cs.findIndex((c) => c.key === over.id);
      return arrayMove(cs, oldI, newI);
    });
  };

  const validCountries = countries.filter((c) => c.name.trim());

  const launch = async () => {
    setSaving(true);
    try {
      await api.post("/trip/build", {
        trip_name: trip.trip_name || undefined,
        start_date: trip.start_date || undefined,
        end_date: trip.end_date || undefined,
        currency: trip.currency,
        origin_country: trip.origin_country || undefined,
        origin_city: trip.origin_city || undefined,
        budget_goal_cents: trip.budget_goal ? Math.round(parseFloat(trip.budget_goal) * 100) : 0,
        countries: validCountries.map((c) => ({
          name: c.name, city: null,
          cities: (c.city || "").split(",").map((s) => ({ name: s.trim() })).filter((x) => x.name),
          arrive_date: c.arrive_date || null, depart_date: c.depart_date || null,
          highlight: c.highlight || null, currency: c.currency || null, language: c.language || null,
        })),
        replace_countries: true,
      });
      await refreshConfig();
      await refreshOrg();
      await refreshTrip();
      toast.success("Your mission trip is live!");
      navigate("/app/itinerary");
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen bg-cream" style={{ paddingTop: "env(safe-area-inset-top)" }}>
      <div className="mx-auto max-w-xl px-5 py-6">
        <button data-testid="build-back-btn" onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-[var(--brand-navy)] mb-4"><ArrowLeft className="h-4 w-4" /> Back</button>

        <h1 className="font-heading font-extrabold text-3xl text-[var(--brand-navy)]">Build your mission trip</h1>
        <p className="text-sm text-muted-foreground mt-1 mb-6">A few quick steps to map out where you're going.</p>

        <div className="flex items-center gap-2 mb-6">
          {[1, 2, 3].map((s) => (
            <div key={s} className={`h-1.5 flex-1 rounded-full ${s <= step ? "accent-bg" : "bg-muted"}`} />
          ))}
        </div>

        <div className="bg-white rounded-3xl p-6 shadow-[0_8px_32px_rgba(44,44,62,0.06)] animate-fade-up">
          {step === 1 && (
            <div className="space-y-4">
              <h2 className="font-heading font-bold text-xl text-[#2C2C3E]">Trip basics</h2>
              <F label="Trip name" testid="bt-name" value={trip.trip_name} onChange={(e) => setTrip({ ...trip, trip_name: e.target.value })} placeholder="RESET 2026 Mission" />
              <div className="grid grid-cols-2 gap-3">
                <F label="Start date" testid="bt-start" type="date" value={trip.start_date || ""} onChange={(e) => setTrip({ ...trip, start_date: e.target.value })} />
                <F label="End date" testid="bt-end" type="date" value={trip.end_date || ""} onChange={(e) => setTrip({ ...trip, end_date: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <F label="Currency" testid="bt-currency" value={trip.currency} onChange={(e) => setTrip({ ...trip, currency: e.target.value })} />
                <F label="Budget goal" testid="bt-goal" type="number" value={trip.budget_goal} onChange={(e) => setTrip({ ...trip, budget_goal: e.target.value })} />
              </div>
              <div className="rounded-2xl bg-muted/40 p-3 space-y-3">
                <div className="flex items-center gap-2 text-sm font-medium text-[#2C2C3E]"><MapPin className="h-4 w-4 accent-text" /> Where does the journey begin? <span className="text-muted-foreground font-normal">(home base)</span></div>
                <div className="grid grid-cols-2 gap-3">
                  <F label="Home country" testid="bt-origin-country" value={trip.origin_country} onChange={(e) => setTrip({ ...trip, origin_country: e.target.value })} placeholder="United States" />
                  <F label="Home city (optional)" testid="bt-origin-city" value={trip.origin_city} onChange={(e) => setTrip({ ...trip, origin_city: e.target.value })} placeholder="Los Angeles" />
                </div>
                <p className="text-[11px] text-muted-foreground">This is your departure point — it appears as a home marker where the route begins.</p>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <div>
                <h2 className="font-heading font-bold text-xl text-[#2C2C3E]">Where are you going?</h2>
                <p className="text-sm text-muted-foreground">Add each stop in the order you'll visit. Drag <GripVertical className="inline h-3.5 w-3.5" /> to reorder — partners see this as your route.</p>
              </div>
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
                <SortableContext items={countries.map((c) => c.key)} strategy={verticalListSortingStrategy}>
                  <div className="space-y-4">
                    {countries.map((c, i) => (
                      <SortableCountryRow key={c.key} c={c} i={i} setC={setC} delC={delC} canDelete={countries.length > 1} />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
              <Button data-testid="bt-add-country" onClick={addC} variant="outline" className="rounded-full w-full border-dashed"><Plus className="h-4 w-4 mr-1" /> Add another stop</Button>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4">
              <h2 className="font-heading font-bold text-xl text-[#2C2C3E]">Review & launch</h2>
              <div className="rounded-2xl bg-muted/40 p-4">
                <div className="font-heading font-bold text-lg text-[var(--brand-navy)]">{trip.trip_name || "Your trip"}</div>
                <div className="text-sm text-muted-foreground">{[trip.start_date, trip.end_date].filter(Boolean).join(" → ") || "Dates TBD"}</div>
                <div className="text-sm text-muted-foreground mt-1">Goal: {trip.budget_goal ? `${trip.currency} ${Number(trip.budget_goal).toLocaleString()}` : "—"}</div>
              </div>
              <div className="space-y-2">
                {validCountries.length === 0 && <p className="text-sm text-muted-foreground">No stops added yet — go back to step 2.</p>}
                {validCountries.map((c, i) => (
                  <div key={c.key} className="flex items-start gap-3 rounded-2xl border p-3" data-testid="bt-review-country">
                    <span className="h-7 w-7 rounded-full bg-[var(--brand-navy)]/10 text-[var(--brand-navy)] text-sm font-bold flex items-center justify-center shrink-0">{i + 1}</span>
                    <div className="min-w-0">
                      <div className="font-semibold text-[#2C2C3E] flex items-center gap-1"><Globe2 className="h-4 w-4 accent-text" /> {[c.city, c.name].filter(Boolean).join(", ")}</div>
                      {(c.arrive_date || c.depart_date) && <div className="text-xs text-muted-foreground flex items-center gap-1"><Calendar className="h-3 w-3" />{[c.arrive_date, c.depart_date].filter(Boolean).join(" → ")}</div>}
                      {c.highlight && <div className="text-sm text-[#2C2C3E]/80 mt-0.5">{c.highlight}</div>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex gap-3 mt-6">
            {step > 1 && <Button variant="outline" data-testid="bt-back" className="rounded-full flex-1 h-11" onClick={() => setStep(step - 1)}>Back</Button>}
            {step < 3 ? (
              <Button data-testid="bt-next" className="rounded-full flex-1 h-11 accent-bg text-white hover:opacity-90" onClick={() => setStep(step + 1)}>Continue</Button>
            ) : (
              <Button data-testid="bt-launch" disabled={saving} className="rounded-full flex-1 h-11 accent-bg text-white hover:opacity-90" onClick={launch}><Check className="h-4 w-4 mr-1" /> {saving ? "Launching…" : "Launch trip"}</Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function SortableCountryRow({ c, i, setC, delC, canDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: c.key });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.6 : 1 };
  return (
    <div ref={setNodeRef} style={style} className="rounded-2xl border p-4 space-y-3 bg-white" data-testid="bt-country-row">
      <div className="flex items-center gap-2">
        <button type="button" data-testid={`bt-drag-${i}`} className="touch-none cursor-grab text-muted-foreground hover:text-[var(--brand-navy)] shrink-0" {...attributes} {...listeners}><GripVertical className="h-5 w-5" /></button>
        <span className="h-7 w-7 rounded-full accent-bg text-white text-sm font-bold flex items-center justify-center shrink-0">{i + 1}</span>
        <div className="grid grid-cols-2 gap-2 flex-1 min-w-0">
          <Input data-testid={`bt-country-name-${i}`} value={c.name} onChange={(e) => setC(c.key, "name", e.target.value)} placeholder="Country" className="rounded-xl h-10" />
          <Input data-testid={`bt-country-city-${i}`} value={c.city} onChange={(e) => setC(c.key, "city", e.target.value)} placeholder="Cities (comma-separated)" className="rounded-xl h-10" />
        </div>
        {canDelete && <button data-testid={`bt-del-country-${i}`} onClick={() => delC(c.key)} className="text-muted-foreground hover:text-destructive shrink-0"><Trash2 className="h-4 w-4" /></button>}
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div><Label className="text-xs text-muted-foreground">Arrive</Label><Input data-testid={`bt-arrive-${i}`} type="date" value={c.arrive_date} onChange={(e) => setC(c.key, "arrive_date", e.target.value)} className="rounded-xl h-10 mt-1" /></div>
        <div><Label className="text-xs text-muted-foreground">Depart</Label><Input data-testid={`bt-depart-${i}`} type="date" value={c.depart_date} onChange={(e) => setC(c.key, "depart_date", e.target.value)} className="rounded-xl h-10 mt-1" /></div>
      </div>
      <Textarea data-testid={`bt-highlight-${i}`} value={c.highlight} onChange={(e) => setC(c.key, "highlight", e.target.value)} placeholder="Why here? (shared with partners)" className="rounded-xl min-h-[60px]" />
    </div>
  );
}

function F({ label, testid, ...props }) {
  return (
    <div>
      <Label className="text-sm text-[#2C2C3E]">{label}</Label>
      <Input data-testid={testid} className="mt-1.5 rounded-xl h-11" {...props} />
    </div>
  );
}
